import * as AF from "@/components/form-items/AbstractForm";
import { Social, SocialType, socialTypes } from "@/lib/cms/cms-types";
import { Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { socialToIcon } from "../icons/socials/SocialIcon";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Separator } from "../ui/separator";

export const GeneralSettingsForm = () => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col rounded-2xl bg-gray-50 border border-gray-300 p-4 items-center justify-between gap-2">
      <p className="text-lg font-medium text-gray-600">
        {t("cms.generalSettings.title")}
      </p>
      <AF.Col>
        <AF.Switch
          name="headerAlwaysVisible"
          label={t("cms.generalSettings.headerAlwaysVisibleDesktop")}
        />
        <AF.Switch
          name="headerAlwaysVisibleMobile"
          label={t("cms.generalSettings.headerAlwaysVisibleMobile")}
        />
        <Separator />
        <Socials />
      </AF.Col>
    </div>
  );
};

type SocialItemProps = {
  social: Social;
  index: number;
  handleRemoveSocial: (index: number) => void;
};

const SocialItem = ({ social, index, handleRemoveSocial }: SocialItemProps) => {
  const { t } = useTranslation();

  const SocialIcon = socialToIcon[social.socialType];

  return (
    <AF.Col
      className="p-3 rounded-xl border border-gray-200 bg-white"
      key={social.socialType}
    >
      <AF.Row className="justify-between pr-5">
        <SocialIcon
          className="w-10 h-10"
          color={social.colored ? undefined : "#9ca3af"}
        />
        <div className="flex w-full gap-6 items-center">
          <AF.Col className="w-1/2">
            <AF.Text
              name={`socials.${index}.url`}
              label={t(
                `cms.generalSettings.${social.socialType.toLowerCase()}`,
              )}
            />
          </AF.Col>
          <AF.Col className="gap-2 w-fit">
            <AF.Switch
              className="w-fit"
              name={`socials.${index}.visible`}
              label={t("cms.generalSettings.visible")}
            />
            <AF.Switch
              className="w-fit"
              name={`socials.${index}.colored`}
              label={t("cms.generalSettings.colored")}
            />
          </AF.Col>
        </div>
        <button
          className="text-gray-400 cursor-pointer hover:text-red-500"
          onClick={() => handleRemoveSocial(index)}
        >
          <Trash2 className="w-6 h-6" />
        </button>
      </AF.Row>
    </AF.Col>
  );
};

const Socials = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const { setValue } = useFormContext();
  const socials = useWatch({ name: "socials" });

  const handleAddSocial = (socialType: SocialType) => {
    const newSocial: Social = {
      socialType,
      url: "",
      visible: true,
      colored: true,
    };
    setValue("socials", [...socials, newSocial]);
    setOpen(false);
  };

  const handleRemoveSocial = (index: number) => {
    const currentSocials: Social[] = socials ?? [];
    const updated = currentSocials.filter((_, i) => i !== index);
    setValue("socials", updated, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="font-medium text-center text-gray-600">
        {t("cms.generalSettings.socials")}
      </p>
      {socials?.map((social: Social, index: number) => (
        <SocialItem
          key={social.socialType}
          social={social}
          index={index}
          handleRemoveSocial={handleRemoveSocial}
        />
      ))}
      {socials && socials.length < socialTypes.length && (
        <AddSocialDialog
          open={open}
          setOpen={setOpen}
          handleAddSocial={handleAddSocial}
        />
      )}
    </div>
  );
};

type AddSocialDialogProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  handleAddSocial: (socialType: SocialType) => void;
};

const AddSocialDialog = ({
  open,
  setOpen,
  handleAddSocial,
}: AddSocialDialogProps) => {
  const { t } = useTranslation();

  const socials = useWatch({ name: "socials" });

  const availableSocialTypes = socialTypes.filter(
    (socialType) =>
      !socials.some((social: Social) => social.socialType === socialType),
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <div className="flex items-center cursor-pointer hover:underline text-brand-600">
          <Plus className="w-4 h-4" />
          <p className="text-sm">{t("cms.generalSettings.addSocial")}</p>
        </div>
      </DialogTrigger>
      <DialogContent needCloseButton={false} className="w-fit">
        <DialogHeader>
          <DialogTitle className="text-center">
            {t("cms.generalSettings.addSocial")}
          </DialogTitle>
        </DialogHeader>
        <div className="pt-4 flex flex-wrap gap-6 justify-center items-center">
          {availableSocialTypes.map((socialType) => {
            const SocialIcon = socialToIcon[socialType];
            return (
              <SocialIcon
                key={socialType}
                className="w-16 h-16 cursor-pointer hover:scale-110 transition-all duration-300"
                onClick={() => handleAddSocial(socialType)}
              />
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
